<template>
  <div v-if="!loading" class="squawks-container">
    <div class="squawks-header">
      <div class="left-section">
        <div class="squawks-tab">Squawks</div>
        <HiloButton
          v-if="canAddSquawks"
          label="Add +"
          :style="{ marginLeft: '20px' }"
          @click="navigateToSquawk('new', { id: 0 })"
        />
      </div>
      <div class="right-section">
        <div class="filter-container">
          <div v-if="filterMenuVisible" class="filter-menu">
            <button
              class="filter-option"
              :class="{ selected: filter === 'all' }"
              @click="setFilter('all')"
            >
              All
            </button>
            <button
              class="filter-option"
              :class="{ selected: filter === 'open' }"
              @click="setFilter('open')"
            >
              Open
            </button>
            <button
              class="filter-option"
              :class="{ selected: filter === 'grounded' }"
              @click="setFilter('grounded')"
            >
              Grounded
            </button>
            <button
              class="filter-option"
              :class="{ selected: filter === 'deferred' }"
              @click="setFilter('deferred')"
            >
              Deferred
            </button>
            <button
              class="filter-option"
              :class="{ selected: filter === 'completed' }"
              @click="setFilter('completed')"
            >
              Completed
            </button>
            <select
              v-if="!id"
              class="filter-option"
              v-model="selectedTailNumberFilter"
              @change="handleTailNumberChange"
            >
              <option value="" disabled selected>Tail #</option>
              <option v-for="tn in tailNumbers" :key="tn" :value="tn">
                {{ tn }}
              </option>
              <option value="clear">Clear</option>
            </select>
          </div>
          <i
            class="fa-solid fa-filter filter-icon"
            @click="toggleFilterMenu"
          ></i>
        </div>
      </div>
    </div>
    <table class="squawks-table">
      <tbody>
        <tr
          v-for="(squawk, index) in filteredSquawksList"
          :key="squawk.description"
        >
          <td class="icon-column">
            <hilo-button :styleType="getSquawkIcon(squawk.status)" disabled />
          </td>
          <td
            v-if="!id"
            class="tail-number-column"
            :style="'border-left: none'"
          >
            {{ squawk.tailNumber }}
          </td>
          <td class="description-column" :style="id ? 'border-left: none' : ''">
            <div class="description-text">{{ squawk.description }}</div>
            <div>
              <span class="name" :ref="'descriptionLabel' + index"
                >Submitted:</span
              >
              <span>{{ squawk.descriptionName }}</span>
            </div>
            <div class="small-text">
              <span :ref="'descriptionDate' + index"
                >Date: {{ formatDate(squawk.descriptionDate) }}</span
              >
              <span :ref="'descriptionCert' + index"
                >Cert#: {{ squawk.descriptionCert }}</span
              >
            </div>
          </td>
          <td class="response-column">
            <div class="response-text">{{ squawk.response }}</div>
            <div v-if="squawk.response">
              <div v-if="squawk.isMechanicResponse">
                <span class="name" :ref="'responseLabel' + index"
                  >Mechanic:</span
                >
                <span>{{ squawk.responseName }}</span>
              </div>
              <div v-else>
                <span class="name" :ref="'responseLabel' + index"
                  >Submitted:</span
                >
                <span>{{ squawk.responseName }}</span>
              </div>
              <div class="small-text">
                <span :ref="'responseDate' + index"
                  >Date: {{ formatDate(squawk.responseDate) }}</span
                >
                <span :ref="'responseCert' + index"
                  >Cert#: {{ squawk.responseCert }}</span
                >
              </div>
            </div>
          </td>
          <td class="action-buttons-column">
            <div class="button-container">
              <hilo-button
                label="View"
                @click="navigateToSquawk('view', squawk)"
              />
              <hilo-button
                v-if="canUpdateSquawks && squawk.status !== 'completed'"
                label="Update"
                @click="navigateToSquawk('update', squawk)"
              />
              <!--              v2-->
              <!--              <hilo-button-->
              <!--                v-if="squawk.status !== 'completed'"-->
              <!--                label="Upload Video/Photo"-->
              <!--              />-->
              <!--              <hilo-button-->
              <!--                v-if="squawk.status === 'completed'"-->
              <!--                label="View Video/Photo"-->
              <!--              />-->
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import SquawksAPIService from "src/servicehandlers/SquawksAPIService.js";
const squawkAPIService = new SquawksAPIService();
import dayjs from "dayjs";
import HiloButton from "src/components/HiloComponents/HiloButton.vue";
import { useLoginStore } from "@/stores/loginStore";

export default {
  components: { HiloButton },
  props: ["id"],

  data() {
    return {
      selectedTab: "Squawks",
      filterMenuVisible: false,
      filter: "all",
      selectedTailNumberFilter: "",
      aircraftTailNumber: "",
      descriptionPadding: 0,
      filteredSquawksList: [],
      loading: true,
    };
  },
  computed: {
    tailNumbers() {
      return [
        ...new Set(this.squawks.map((squawk) => squawk.aircraft.tailNumber)),
      ];
    },
    canUpdateSquawks() {
      const store = useLoginStore();
      return ["Admin", "Maintenance"].includes(store.getUser.userType?.name);
    },
    canAddSquawks() {
      const store = useLoginStore();
      return ["Admin", "Maintenance", "Instructor"].includes(
        store.getUser.userType?.name,
      );
    },
  },
  async mounted() {
    await this.getSquawks();
    await this.updateFilteredSquawks();
    this.loading = false;
  },
  methods: {
    async getSquawks() {
      const squawkServiceMethod = this.id
        ? squawkAPIService.getByAircraftId(this.id, this.$router)
        : squawkAPIService.get(this.$router);

      return squawkServiceMethod
        .then((response) => {
          this.squawks = response.map((squawk) => ({
            ...squawk,
            status: squawk.squawkStatus.name,
            tailNumber: squawk.aircraft.tailNumber,
            descriptionName: `${squawk.descriptionUser.firstName} ${squawk.descriptionUser.lastName}`,
            descriptionDate: squawk.descriptionDate,
            descriptionCert: squawk.descriptionUser.cert,
            isMechanicResponse: squawk.isMechanicResponse,
            responseName: `${squawk.responseUser?.firstName} ${squawk.responseUser?.lastName}`,
            responseDate: squawk.responseDate,
            responseCert: squawk.responseUser?.cert,
          }));
          this.aircraftTailNumber = this.id
            ? this.squawks[0].tailNumber
            : undefined;
        })
        .catch(() => {});
    },
    selectTab(tab) {
      this.selectedTab = tab;
    },
    navigateToSquawk(type, squawk) {
      this.$router.push({
        path: `/hilo/aircraft/${this.aircraftTailNumber}/squawk`,
        query: {
          type: type,
          id: squawk.id,
        },
      });
    },
    getSquawkIcon(status) {
      switch (status) {
        case "Completed":
          return "greenCheckIcon";
        case "Deferred":
          return "orangeClockIcon";
        case "Grounded":
          return "redXIcon";
        default:
          return "";
      }
    },
    formatDate(date) {
      return dayjs(date).format("MM/DD/YY");
    },
    toggleFilterMenu() {
      this.filterMenuVisible = !this.filterMenuVisible;
    },
    setFilter(filter) {
      if (filter === "all") {
        this.selectedTailNumberFilter = "";
        this.filter = "all";
      } else if (this.tailNumbers.includes(filter)) {
        this.selectedTailNumberFilter = filter;
        this.filter = "";
      } else {
        this.filter = filter;
      }
      this.updateFilteredSquawks();
    },
    handleTailNumberChange(event) {
      const value = event.target.value;
      if (value === "clear") {
        this.setFilter("all");
      } else {
        this.setFilter(value);
      }
    },
    async updateFilteredSquawks() {
      this.filteredSquawksList = this.filterSquawks();
      this.$nextTick(() => {
        setTimeout(() => {
          this.updateCertPadding(this.filteredSquawksList);
        }, 0);
      });
    },
    filterSquawks() {
      if (this.filter === "all") return this.squawks;
      return this.squawks.filter((squawk) => {
        const statusMatch =
          this.filter === "all" ||
          squawk.status === this.filter ||
          this.filter === "" ||
          (this.filter === "open" &&
            (squawk.status === "grounded" || squawk.status === "deferred"));
        const tailNumberMatch =
          !this.selectedTailNumberFilter ||
          squawk.tailNumber === this.selectedTailNumberFilter;
        return tailNumberMatch && statusMatch;
      });
    },
    updateCertPadding(list) {
      list.forEach((_, index) => {
        const submitLabel = this.$refs["descriptionLabel" + index][0];
        const submitDate = this.$refs["descriptionDate" + index][0];
        const cert = this.$refs["descriptionCert" + index][0];

        if (submitLabel && submitDate && cert) {
          const labelWidth = submitLabel.offsetWidth;
          const labelMarginRight = parseFloat(
            window.getComputedStyle(submitLabel).marginRight,
          );
          const dateWidth = submitDate.offsetWidth;

          const paddingLeft = labelWidth + labelMarginRight - dateWidth;
          cert.style.marginLeft = `${paddingLeft}px`;
        }

        if (this.$refs["responseLabel" + index]) {
          const responseLabel = this.$refs["responseLabel" + index][0];
          const responseDate = this.$refs["responseDate" + index][0];
          const responseCert = this.$refs["responseCert" + index][0];

          if (responseLabel && responseDate && responseCert) {
            const responseLabelWidth = responseLabel.offsetWidth;
            const responseLabelMarginRight = parseFloat(
              window.getComputedStyle(responseLabel).marginRight,
            );
            const responseDateWidth = responseDate.offsetWidth;

            const responsePaddingLeft =
              responseLabelWidth + responseLabelMarginRight - responseDateWidth;
            responseCert.style.marginLeft = `${responsePaddingLeft}px`;
          }
        }
      });
    },
  },
};
</script>

<style scoped>
.squawks-container {
  background-color: white;
  margin-top: 30px;
  border-radius: 8px;
}

.squawks-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.left-section {
  display: flex;
  align-items: center;
}

.right-section {
  display: flex;
  align-items: center;
}

.squawks-tab {
  background-color: #888;
  color: white;
  padding: 5px 30px;
  font-size: 16px;
  border-radius: 8px 8px 0 0;
  cursor: pointer;
}

.filter-container {
  position: relative;
  display: inline-block;
  margin-top: -10px;
}

.filter-menu {
  display: flex;
  position: absolute;
  top: 0;
  right: 50px;
  background-color: white;
  border: 1px solid lightgrey;
  border-radius: 16px;
  padding: 4px;
}

.filter-option {
  background-color: white;
  border: 1px solid black;
  border-radius: 16px;
  padding: 2px 12px;
  cursor: pointer;
  font-size: 12px;
  margin: 0 5px;
}

.filter-option:hover {
  background-color: #f0f0f0;
}

.filter-option.selected {
  background-color: black;
  color: white;
}

.squawks-table {
  width: 100%;
  border: 1px solid lightgrey;
  border-collapse: collapse;
}

.squawks-table tr {
  border-bottom: 1px solid lightgrey;
}

.squawks-table td {
  padding: 10px 20px 10px 10px;
  vertical-align: top;
  border-left: 1px solid lightgrey;
}

.icon-column {
  width: 2%;
  margin-top: 5px;
}

.tail-number-column {
  width: 5%;
}

.description-column {
  width: 38%;
}

.response-column {
  width: 38%;
}

.action-buttons-column {
  width: auto;
}

.button-container {
  float: right;
  display: flex;
  gap: 10px;
}

.filter-icon {
  font-size: 18px;
  cursor: pointer;
  margin: 0 10px 0 20px;
  padding: 8px 8px 5px;
  border: 1px solid lightgrey;
  border-radius: 50%;
  display: inline-flex;
  justify-content: center;
  align-items: center;
}

.description-text,
.response-text {
  margin-bottom: 20px;
}

.small-text {
  font-size: 9px;
  margin-top: -4px;
  color: grey;
}

.name {
  margin-right: 30px;
}
</style>
